import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMyProfile, queryKeys } from '@/hooks/api';
import { useUI, useAppStore } from '@/store';
import { Button, LoadingScreen } from '@/components/ui';
import { TelegramUtils } from '@/utils/telegram';
import { cn, getBadgeLevelColor, getScoreColor, capitalizeBadgeLevel, Linkify } from '@/utils';
import { api } from '@/api';

const ProfileScreen: React.FC = () => {
  const { setActiveScreen } = useUI();
  const { data: profile, isLoading } = useMyProfile();
  const { setUser } = useAppStore();
  const queryClient = useQueryClient();
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  useEffect(() => {
    TelegramUtils.configureBackButton(true, () => {
      setActiveScreen('feed');
    });
    TelegramUtils.configureMainButton({ visible: false });
  }, [setActiveScreen]);

  const handleBecomeIT = async () => {
    const confirmed = await TelegramUtils.showConfirm(
      'Если вы станете IT-специалистом, нужно будет:\n• Выбрать специализацию\n• Пройти тест верификации\n\nПродолжить?'
    );

    if (!confirmed) return;

    try {
      TelegramUtils.haptic.light();
      
      // Update is_it to true
      await (api as any).updateBasicInfo({ is_it: true });
      
      // Refresh profile
      const updatedProfile = await api.getMyProfile();
      setUser(updatedProfile);
      queryClient.invalidateQueries({ queryKey: queryKeys.myProfile });
      
      TelegramUtils.haptic.success();
      
      // Navigate to update specialization
      setActiveScreen('update-specialization');
    } catch (error) {
      console.error('Failed to become IT:', error);
      TelegramUtils.haptic.error();
      await TelegramUtils.showAlert('Ошибка при обновлении статуса. Попробуйте позже.');
    }
  };

  if (isLoading) {
    return <LoadingScreen message="Загружаем ваш профиль..." />;
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-tg-bg">
        <p className="text-white">Профиль не найден</p>
      </div>
    );
  }

  const isMale = profile.role !== undefined;
  
  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => 
      prev < profile.photos.length - 1 ? prev + 1 : 0
    );
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => 
      prev > 0 ? prev - 1 : profile.photos.length - 1
    );
  };

  return (
    <div className="min-h-screen bg-tg-bg safe-area-y pb-6">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <button
          onClick={() => {
            setActiveScreen('feed');
            TelegramUtils.haptic.light();
          }}
          className="p-2 -ml-2 hover:bg-white/5 rounded-xl transition-colors"
        >
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <h1 className="text-xl font-bold text-white absolute left-1/2 transform -translate-x-1/2">Профиль</h1>
        
        <button
          onClick={() => {
            setActiveScreen('edit-profile');
            TelegramUtils.haptic.light();
          }}
          className="p-2 -mr-2 hover:bg-white/5 rounded-xl transition-colors"
        >
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
      </div>

      {/* Main Photo Section */}
      <div className="relative w-full" style={{ height: 'calc(100vh - 400px)', minHeight: '400px', maxHeight: '600px' }}>
        <div className="relative h-full">
          <img
            src={profile.photos[currentPhotoIndex]}
            alt={profile.name}
            className="w-full h-full object-cover"
          />
          
          {/* Top gradient for indicators visibility */}
          <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-black/40 to-transparent" />
          
          {/* Bottom gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          
          {/* Photo Navigation Indicators */}
          {profile.photos.length > 1 && (
            <>
              <div className="absolute top-4 left-4 right-4 flex gap-1 z-10">
                {profile.photos.map((_, index) => (
                  <div
                    key={index}
                    className={cn(
                      'flex-1 h-1 rounded-full transition-colors',
                      index === currentPhotoIndex ? 'bg-white' : 'bg-white/30'
                    )}
                  />
                ))}
              </div>
              
              {/* Navigation touch areas */}
              <button
                className="absolute left-0 top-0 bottom-0 w-1/2 z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  prevPhoto();
                  TelegramUtils.haptic.light();
                }}
              />
              <button
                className="absolute right-0 top-0 bottom-0 w-1/2 z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  nextPhoto();
                  TelegramUtils.haptic.light();
                }}
              />
            </>
          )}
          
          {/* Profile Info Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
            {/* Name and Age */}
            <div className="flex items-baseline gap-2 mb-2">
              <h2 className="text-3xl font-bold text-white">
                {profile.name}
              </h2>
              <span className="text-2xl text-white/80">
                {profile.age}
              </span>
            </div>
            
            {/* Role & Level & Score - for IT users */}
            {isMale && (
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {profile.level && (
                    <span className={cn(
                      'px-2 py-0.5 rounded-full text-sm font-semibold',
                      getBadgeLevelColor(profile.level)
                    )}>
                      {capitalizeBadgeLevel(profile.level)}
                    </span>
                  )}
                  <span className="text-white/70">
                    {profile.role}
                  </span>
                  {profile.verified && profile.itTestScore !== undefined && (
                    <span className={cn('font-bold', getScoreColor(profile.itTestScore))}>
                      • {profile.itTestScore}
                    </span>
                  )}
                </div>
                {profile.company && (
                  <p className="text-white/60 text-sm">
                    {profile.company}
                  </p>
                )}
              </div>
            )}
            
            {/* Desired Lifestyle Tag */}
            {profile.desiredLifestyle && (
              <div className="mb-3">
                <span className="text-sm text-white/80 bg-white/10 px-3 py-1.5 rounded-full">
                  {profile.desiredLifestyle}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="px-4 mt-6 space-y-6">
        {/* Bio */}
        {profile.bio && (
          <div>
            <h3 className="text-sm font-semibold text-white/50 light:text-neutral-500 uppercase tracking-wider mb-2">
              О себе
            </h3>
            <p className="text-white/90 light:text-neutral-900 leading-relaxed whitespace-pre-wrap">
              <Linkify>{profile.bio}</Linkify>
            </p>
          </div>
        )}

        {/* Location */}
        {(profile.city || profile.country) && (
          <div>
            <h3 className="text-sm font-semibold text-white/50 light:text-neutral-500 uppercase tracking-wider mb-2">
              Местоположение
            </h3>
            <p className="text-white/90 light:text-neutral-900">
              📍 {[profile.city, profile.country].filter(Boolean).join(', ')}
            </p>
          </div>
        )}

        {/* Meeting Goals */}
        {profile.meetingGoals && profile.meetingGoals.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-white/50 light:text-neutral-500 uppercase tracking-wider mb-3">
              Цели знакомства
            </h3>
            <div className="flex flex-wrap gap-2">
              {profile.meetingGoals.map((goal) => (
                <span 
                  key={goal.id}
                  className="px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-sm text-white font-medium border border-white/10"
                >
                  {goal.icon} {goal.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Tech Stack - for males only */}
        {isMale && profile.techStack && profile.techStack.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-white/50 light:text-neutral-500 uppercase tracking-wider mb-3">
              Tech Stack
            </h3>
            <div className="flex flex-wrap gap-2">
              {profile.techStack.map((tech) => (
                <span 
                  key={tech} 
                  className="px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-sm text-white font-medium border border-white/10"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Hobbies/Interests */}
        {profile.interests && profile.interests.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-white/50 light:text-neutral-500 uppercase tracking-wider mb-3">
              Интересы
            </h3>
            <div className="flex flex-wrap gap-2">
              {profile.interests.map((interest) => (
                <span 
                  key={interest} 
                  className="px-3 py-1.5 bg-white/5 backdrop-blur-sm rounded-full text-sm text-white/70 border border-white/10"
                >
                  {interest}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Lifestyle - if exists (from old data structure) */}
        {profile.lifestyle && profile.lifestyle.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-white/50 light:text-neutral-500 uppercase tracking-wider mb-3">
              Хобби
            </h3>
            <div className="flex flex-wrap gap-2">
              {profile.lifestyle.map((item) => (
                <span 
                  key={item} 
                  className="px-3 py-1.5 bg-white/5 backdrop-blur-sm rounded-full text-sm text-white/70 border border-white/10"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        {profile.stats && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 light:bg-neutral-100 light:border-neutral-300">
            <h3 className="text-sm font-semibold text-white/50 light:text-neutral-500 uppercase tracking-wider mb-4">
              Статистика
            </h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="text-2xl font-bold text-white light:text-neutral-900 mb-1">
                  {profile.stats.likes || 0}
                </div>
                <p className="text-sm text-white/60 light:text-neutral-600">Лайков</p>
              </div>
              <div>
                <div className="text-2xl font-bold text-white light:text-neutral-900 mb-1">
                  {profile.stats.matches || 0}
                </div>
                <p className="text-sm text-white/60 light:text-neutral-600">Совпадений</p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="pt-4 space-y-3">
          {/* Skills & Verification Button - for IT users */}
          {profile.is_it === true && (
            <button
              onClick={() => {
                setActiveScreen('skills-verification');
                TelegramUtils.haptic.light();
              }}
              className="w-full h-14 bg-white/10 border border-white/20 rounded-2xl text-white font-semibold hover:bg-white/20 transition-all active:scale-95 flex items-center justify-between px-4"
            >
              <span>Навыки и верификация</span>
              <svg className="w-5 h-5 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Become IT Button - for non-IT females */}
          {profile.gender === 'female' && profile.is_it === false && (
            <button
              onClick={handleBecomeIT}
              className="w-full h-14 bg-primary-500/20 border-2 border-primary-500/40 rounded-2xl text-primary-300 font-bold hover:bg-primary-500/30 transition-all active:scale-95"
            >
              💻 Я из IT!
            </button>
          )}

          <button
            onClick={() => {
              setActiveScreen('settings');
              TelegramUtils.haptic.light();
            }}
            className="w-full h-14 bg-white/10 border border-white/20 rounded-2xl text-white font-semibold hover:bg-white/20 transition-all active:scale-95 flex items-center justify-between px-4"
          >
            <span>Настройки</span>
            <svg className="w-5 h-5 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileScreen;
